"use client"

import { VideoCategorySkeleton } from "@/(admin)/modules/ui-builder/config/themes/tamda-media/TamdaMediaNewsLayout/skeleton/VideoCategorySkeleton"
import { Images } from "@/constants/images"
import { resolveBatchDataEntry, useBatchDataMap } from "@/lib/batch-data-context"
import { buildTamdaMediaVideoCategoryLayoutKey } from "@/lib/extract-batch-requests"
import { formatTimeAgo } from "@/lib/utils"
import { VideoData } from "@/types/types"
import Image from "next/image"
import Link from "next/link"
import { useState } from "react"
import { twMerge } from "tailwind-merge"
import { VideoCategoryLayoutProps } from "."
import VideoModal from "../../../components/VideoModal"
import { generateResponsiveCSS } from "../../../lib/helper"
import { getYouTubeThumbnail } from "../../../lib/youtube-helpers"
import { useStableComponentId } from "../TamdaMediaNewsLayout/hooks/useStableComponentId"

export const RenderConfig = {
  render: ({ category, id: componentId }: VideoCategoryLayoutProps & { id?: string }) => {
    const id = useStableComponentId(componentId)
    const responsiveCSS = generateResponsiveCSS(id, {}, "desktop")
    const batchData = useBatchDataMap()
    const videoEntry = resolveBatchDataEntry<{
      data?: VideoData[]
      meta?: { headerTitle?: string; slugCategory?: string }
    }>(batchData, {
      key: buildTamdaMediaVideoCategoryLayoutKey(id),
      prefix: "tamda-media-video-category-layout:",
    })
    const videos = Array.isArray(videoEntry?.data) ? videoEntry.data : []
    const loading = !!category && videos.length === 0
    const headerTitle =
      videoEntry?.meta?.headerTitle || (typeof category === "string" ? "Video" : category?.categoryName) || "Video"
    const slugCategory = videoEntry?.meta?.slugCategory || (typeof category === "string" ? "" : category?.slug) || ""
    const [modalVideo, setModalVideo] = useState<string>("")
    const [isModalOpen, setIsModalOpen] = useState(false)

    const openModal = (youtubeUrl: string) => {
      setModalVideo(youtubeUrl)
      setIsModalOpen(true)
    }

    const closeModal = () => {
      setIsModalOpen(false)
      setModalVideo("")
    }

    const renderItem = (item: VideoData) => {
      const title = item.attributes?.title
      const youtubeUrl = item.attributes?.youtubeUrl || (item as any)?.attributes?.url || ""
      const thumb = getYouTubeThumbnail(youtubeUrl, "hqdefault")
      const videoPublishedAt = item.attributes?.videoPublishedAt
      const videoPublishedAtText = formatTimeAgo(videoPublishedAt)
      return (
        <div
          key={item.id}
          className="flex gap-[15px] cursor-pointer group image-hover sm:h-[72px]"
          onClick={() => openModal(youtubeUrl)}
        >
          <div className="relative sm:w-[25.8%] w-[26.1%] sm:h-[72px]">
            <div className="w-full h-full relative">
              <Image src={thumb} alt={title} fill className="object-cover" sizes="(max-width: 640px) 26vw, 25vw" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 relative">
                <Image src={Images.playIconWhite} alt="play" fill className="object-cover" sizes="32px" />
              </div>
            </div>
          </div>
          <div className="sm:w-[74.2%] w-[73.9%]">
            <h3 className="text-sm leading-[19.6px] text-black sm:font-semibold font-medium hover:text-[#1EAD46] transition-all duration-300 line-clamp-3">
              {title}
            </h3>
            <p className="sm:text-xs sm:leading-[18.6px] text-[10px] leading-[15.5px] text-black font-normal opacity-50">
              {videoPublishedAtText}
            </p>
          </div>
        </div>
      )
    }

    const FeaturedVideo = ({ item }: { item: VideoData }) => {
      const title = item.attributes?.title
      const youtubeUrl = item.attributes?.youtubeUrl || (item as any)?.attributes?.url || ""
      const thumb = getYouTubeThumbnail(youtubeUrl, "maxresdefault")
      return (
        <div className="w-full">
          <div
            className="relative group w-full aspect-video sm:h-[370px] h-[192px] cursor-pointer"
            onClick={() => openModal(youtubeUrl)}
          >
            <Image src={thumb} alt={title} fill className="object-cover" sizes="(max-width: 640px) 100vw, 59vw" />
            <h2 className="absolute top-4 left-4 text-base font-semibold text-white z-[1]">{title}</h2>
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
              <div className="w-[120px] h-[120px] relative">
                <Image
                  src={Images.playIcon}
                  alt="play"
                  fill
                  className="object-cover group-hover:brightness-150 transition-all duration-300"
                  sizes="120px"
                />
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <>
        {responsiveCSS}
        <div id={id} className={`${twMerge(id)} container mx-auto`}>
          <div className="flex flex-col sm:gap-[18px] gap-2">
            <div className="bg-[#F5F5F5] flex items-center gap-[18px] h-11 px-2.5">
              <div className="w-[56px] h-[20px]">
                <Image
                  src={Images.subLogo}
                  alt="sub-logo"
                  width={56}
                  height={20}
                  className="w-full h-full object-cover"
                />
              </div>
              <Link
                href={`/category/${slugCategory}`}
                className="sm:text-lg sm:leading-[27.9px] text-base leading-[24.8px] sm:font-bold font-semibold text-[#25262b] hover:text-[#1EAD46]"
              >
                {headerTitle}
              </Link>
            </div>
            {loading ? (
              <VideoCategorySkeleton />
            ) : (
              <div className="flex gap-5 w-full sm:flex-row flex-col">
                <div className="sm:w-[59.3%] w-full">
                  {videos[0] ? <FeaturedVideo item={videos[0]} /> : <div className="py-10 text-center">No videos</div>}
                </div>
                <div className="sm:w-[40.7%] w-full flex flex-col gap-5 sm:h-[370px] h-[348px] overflow-y-auto">
                  {videos.slice(1).map(renderItem)}
                </div>
              </div>
            )}
          </div>
        </div>
        <VideoModal isOpen={isModalOpen} videoUrl={modalVideo} onClose={closeModal} />
      </>
    )
  },
}
